// This registration only handles push. It never caches pages or private data.
export const pushSupported =
  "serviceWorker" in navigator &&
  typeof PushManager === "function" &&
  typeof Notification === "function";

let registering: Promise<ServiceWorkerRegistration> | undefined;
export function registerServiceWorker() {
  if (!registering) {
    registering = (async () => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        return await Promise.race([
          navigator.serviceWorker
            .register("/sw.js", { updateViaCache: "none" })
            .then(() => navigator.serviceWorker.ready),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("Service worker unavailable")),
              10000,
            );
          }),
        ]);
      } finally {
        clearTimeout(timer);
      }
    })().catch((error: unknown) => {
      registering = undefined;
      throw error;
    });
  }
  return registering;
}
const registration = () => navigator.serviceWorker.getRegistration("/");
export async function browserSubscription() {
  return (await (await registration())?.pushManager.getSubscription()) ?? null;
}

// Reuse the subscription only while its key and expiry are current. Permission
// is requested by a click handler, never by renewal or service worker startup.
export async function subscribeBrowser(publicKey: string) {
  const { pushManager } = await registerServiceWorker();
  const key = Uint8Array.from(
    atob(publicKey.replaceAll("-", "+").replaceAll("_", "/")),
    (c) => c.charCodeAt(0),
  );
  const current = await pushManager.getSubscription();
  const currentKey = new Uint8Array(
    current?.options.applicationServerKey ?? new ArrayBuffer(0),
  );
  if (
    current &&
    (!current.expirationTime || current.expirationTime > Date.now()) &&
    currentKey.length === key.length &&
    currentKey.every((byte, i) => byte === key[i])
  )
    return current;
  if (current && !(await current.unsubscribe()))
    throw new Error("Could not replace subscription");
  return pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
}
async function closeNotifications(matches: (tag: string) => boolean) {
  for (const notification of (await (
    await registration()
  )?.getNotifications()) ?? [])
    if (matches(notification.tag)) notification.close();
}
export const closeCallNotifications = () =>
  closeNotifications((tag) => tag.startsWith("call:"));
const messageTag = (personId: string) => `message:${personId}`;
// Without a list, every message alert closes.
export const closeMessageNotifications = (people?: string[]) =>
  closeNotifications((tag) =>
    people
      ? people.some((person) => tag === messageTag(person))
      : tag.startsWith("message:"),
  );
